import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Circle, CircleDot, Square, CheckSquare } from 'lucide-react';
import type { PollQuestion, PollAnswer } from '../types';

interface PollQuestionViewProps {
  question: PollQuestion;
  answer: PollAnswer;
  onChange: (answer: PollAnswer) => void;
  disabled?: boolean;
}

export function PollQuestionView({ question, answer, onChange, disabled }: PollQuestionViewProps) {
  const selectedOptions = answer.selectedOptions || [];

  return (
    <Card>
      <CardContent className="pt-5 pb-5">
        <Label className="text-base font-medium leading-relaxed">
          {question.text}
          {question.required && <span className="text-destructive ml-1">*</span>}
        </Label>

        {question.type === 'free_text' ? (
          <div className="mt-3">
            <Textarea
              value={answer.textAnswer || ''}
              onChange={(e) => onChange({ ...answer, textAnswer: e.target.value })}
              placeholder="Type your answer..."
              disabled={disabled}
              className="min-h-[80px]"
            />
          </div>
        ) : (
          <div className="mt-3 space-y-1.5">
            <p className="text-xs text-muted-foreground mb-2">
              {question.type === 'single_choice' ? 'Select one option' : 'Select all that apply'}
            </p>
            {(question.options || []).map((opt, i) => {
              const isChecked = selectedOptions.includes(i);
              const isSingle = question.type === 'single_choice';

              return (
                <button
                  key={i}
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    let newSelected: number[];
                    if (isSingle) {
                      newSelected = isChecked ? [] : [i];
                    } else {
                      newSelected = isChecked
                        ? selectedOptions.filter((idx) => idx !== i)
                        : [...selectedOptions, i];
                    }
                    onChange({ ...answer, selectedOptions: newSelected });
                  }}
                  className={`
                    flex items-center gap-3 w-full rounded-lg border px-4 py-2.5 text-sm text-left transition-all
                    ${
                      isChecked
                        ? 'border-primary bg-primary/5 text-foreground'
                        : 'border-border hover:border-primary/30 hover:bg-accent/50 text-foreground'
                    }
                    ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                >
                  {isSingle ? (
                    isChecked ? (
                      <CircleDot className="h-4 w-4 text-primary shrink-0" />
                    ) : (
                      <Circle className="h-4 w-4 text-muted-foreground/50 shrink-0" />
                    )
                  ) : isChecked ? (
                    <CheckSquare className="h-4 w-4 text-primary shrink-0" />
                  ) : (
                    <Square className="h-4 w-4 text-muted-foreground/50 shrink-0" />
                  )}
                  {opt}
                </button>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
